import React, {
  useCallback, useEffect, useRef,
} from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';

import { useTheme } from 'emotion-theming';
import Text from '../../../../shared/components/Text/Text';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { journalTracker } from '../../../../shared/utils/amplitudeHelper';
import ExerciseDataBaseButton from './ExerciseDataBaseButton';
import JournalAthleteSelect from './JournalAthleteSelect';

import JournalEntryList from '../../../journal/components/JournalEntryList';
import JournalVariantContext from '../../../journal/JournalVariantContext';
import ModalHandler from '../../../journal/ModalHandler';
import SubModalHandler from '../../../journal/SubModalHandler';
import {
  getJournalEntriesForAthlete,
  getMeJournalEntries,
  getUsers,
  setActiveReduxModal,
  setExerciseTab,
  setSelectedAthlete,
} from '../../../journal/ducks/journalActions';

const Wrapper = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 16px;
  position: relative;
  width: 100%;
`;

const TitleContainer = styled('div')`
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const TitleIconsContainer = styled('div')`
  align-items: center;
  display: flex;
  gap: 12px;
`;

const IconButton = styled('button')`
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  padding: 0;
`;

/**
 * -190 leaves room for the title row, the athlete select row, and the
 * absolutely-positioned create-entry button below, matching the budget
 * ExercisesSideBarContent uses for its own scrollable exercise list (see
 * that file's ExerciseWrapper).
 */
const EntryListWrapper = styled('div')`
  max-height: ${(props) => Math.max(props.sideBarContainerHeight - 190, 0)}px;
  overflow: auto;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.scrollbarThumb};
    border-radius: 10px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0);
  }
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.scrollbarThumb} transparent;
`;

const JournalSideBarContent = ({ sideBarContainerHeight }) => {
  const { colors } = useTheme();
  const dispatch = useDispatch();
  const entryListScrollRef = useRef(null);
  const activeTab = useSelector((state) => state.journal.data.activeTab);
  const allAthletes = useSelector((state) => state.journal.data.allAthletes);
  const currentAthlete = useSelector((state) => state.journal.data.currentAthlete);
  const dateRange = useSelector((state) => state.journal.data.dateRange);
  const filterExercise = useSelector((state) => state.journal.data.filterExercise);
  const mediaOnly = useSelector((state) => state.journal.data.mediaOnly);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedReduxAthlete = useSelector(
    (state) => state.calendar.data.selectedReduxAthlete,
  );
  const {
    accountCode, admin, id: currentUserId, pic,
  } = currentUser;
  const coachAccount = admin > 0;

  const scrollToTop = useCallback(() => {
    if (entryListScrollRef.current) {
      entryListScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const handleCreateEntryClick = useCallback(() => {
    dispatch(setActiveReduxModal('create-entry-modal'));
    journalTracker('Build sidebar', 'Create entry modal opened');
  }, [dispatch]);

  const handleFilterClick = useCallback(() => {
    dispatch(setActiveReduxModal('filter-options-modal'));
    journalTracker('Build sidebar', 'Filter options modal opened');
  }, [dispatch]);

  // One-time prefetch for the athlete select and exercise-filter modal. The
  // roster fetch is guarded on allAthletes already being populated, since
  // this panel unmounts on tab switch and would otherwise re-fetch the whole
  // roster every time the Journal tab is reopened.
  useEffect(() => {
    if (accountCode && currentUserId) {
      batch(() => {
        if (coachAccount && !allAthletes?.length) {
          dispatch(getUsers(accountCode));
        }
        dispatch(setExerciseTab('L', accountCode));
      });
    }
  }, [accountCode, allAthletes?.length, coachAccount, currentUserId, dispatch]);

  /**
   * Defaults the journal to whichever athlete is selected in Build, falling
   * back to the coach's own journal when no athlete is selected (viewing a
   * calendar, or nothing selected yet). A manual pick via the athlete select
   * sticks until Build's own athlete selection changes again, since this
   * effect only re-runs when selectedReduxAthlete itself changes.
   */
  useEffect(() => {
    if (!accountCode || !currentUserId) return;
    const hasSelectedAthlete = Object.keys(selectedReduxAthlete || {}).length > 0;
    const targetAthlete = hasSelectedAthlete
      ? {
        first: selectedReduxAthlete.first,
        id: selectedReduxAthlete.userId || selectedReduxAthlete.id,
        last: selectedReduxAthlete.last,
        pic: selectedReduxAthlete.pic || null,
      }
      : {
        first: 'My',
        id: currentUserId,
        last: 'Journal',
        pic: pic || null,
      };
    dispatch(setSelectedAthlete(targetAthlete));
  }, [
    accountCode,
    currentUserId,
    dispatch,
    pic,
    selectedReduxAthlete?.first,
    selectedReduxAthlete?.id,
    selectedReduxAthlete?.last,
    selectedReduxAthlete?.pic,
    selectedReduxAthlete?.userId,
  ]);

  useEffect(() => {
    // Guards against the fetch effect firing once with a not-yet-synced
    // currentAthlete (e.g. on first mount, before the sync effect above has
    // resolved the default) — wait until it's settled to a real athlete id.
    if (!accountCode || !currentUserId || !currentAthlete?.id) return;
    if (!coachAccount || currentAthlete.id === currentUserId) {
      dispatch(getMeJournalEntries(accountCode));
    } else {
      dispatch(getJournalEntriesForAthlete(accountCode));
    }
  }, [
    accountCode,
    coachAccount,
    currentAthlete?.id,
    currentUserId,
    dateRange,
    dispatch,
    filterExercise,
    mediaOnly,
  ]);

  return (
    <JournalVariantContext.Provider value='build'>
      <Wrapper>
        <TitleContainer>
          <Text fontWeight={700} fontSize='13px'>Journal</Text>
          <TitleIconsContainer>
            <IconButton
              type='button'
              onClick={handleFilterClick}
              aria-label='Open journal filter options'
            >
              <IcomoonReact
                iconSet={iconSet}
                size={16}
                icon='filter2'
                color={colors.textSecondary}
                style={{ minWidth: '15px' }}
              />
            </IconButton>
          </TitleIconsContainer>
        </TitleContainer>
        <JournalAthleteSelect scrollToTop={scrollToTop} />
        <EntryListWrapper ref={entryListScrollRef} sideBarContainerHeight={sideBarContainerHeight}>
          <JournalEntryList />
        </EntryListWrapper>
        {activeTab === 'SINGLE_ATHLETE' && (
          <ExerciseDataBaseButton onClick={handleCreateEntryClick} type='Journal Entry' />
        )}
        <ModalHandler scrollToTop={scrollToTop} />
        <SubModalHandler />
      </Wrapper>
    </JournalVariantContext.Provider>
  );
};

JournalSideBarContent.propTypes = {
  sideBarContainerHeight: PropTypes.number.isRequired,
};

export default JournalSideBarContent;
